/**
 * Database Login Page Component
 * Username/password authentication for database-backed users
 */

import { useState } from 'react';
import { LogIn, User, Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/Common';
import { ErrorMessage } from '@/components/Common';
import { appConfig } from '@/config/azureConfig';

export function DatabaseLoginPage() {
  const { loginWithDatabase, login, error, isLoading, isEntraAuthEnabled } =
    useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) return;

    try {
      await loginWithDatabase(username, password);
    } catch (err) {
      // Error is handled in context
      console.error('Database login failed:', err);
    }
  };

  const handleEntraLogin = async () => {
    try {
      await login();
    } catch (err) {
      console.error('Entra ID login failed:', err);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-secondary px-4">
      <div className="w-full max-w-md">
        <div className="rounded-xl bg-bg-primary p-8 shadow-2xl">
          {/* Logo/Header */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-stellar-primary">
              {appConfig.name}
            </h1>
            <p className="mt-2 text-text-secondary">
              Your AI-powered Microsoft 365 assistant
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6">
              <ErrorMessage message={error} severity="error" />
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username field */}
            <div>
              <label
                htmlFor="username"
                className="mb-1 block text-sm font-medium text-text-primary"
              >
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-tertiary" />
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  disabled={isLoading}
                  className="w-full rounded-lg border border-border-default bg-bg-primary py-2.5 pl-10 pr-4 text-text-primary placeholder-text-tertiary transition-colors focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                  autoComplete="username"
                  autoFocus
                />
              </div>
            </div>

            {/* Password field */}
            <div>
              <label
                htmlFor="password"
                className="mb-1 block text-sm font-medium text-text-primary"
              >
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-tertiary" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  className="w-full rounded-lg border border-border-default bg-bg-primary py-2.5 pl-10 pr-4 text-text-primary placeholder-text-tertiary transition-colors focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isLoading}
              leftIcon={<LogIn className="h-5 w-5" />}
              className="w-full"
              disabled={isLoading || !username || !password}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          {/* Entra ID login option */}
          {isEntraAuthEnabled && (
            <>
              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-border-default" />
                <span className="text-sm text-text-tertiary">or</span>
                <div className="h-px flex-1 bg-border-default" />
              </div>

              <Button
                onClick={handleEntraLogin}
                variant="secondary"
                size="lg"
                isLoading={isLoading}
                className="w-full"
                disabled={isLoading}
              >
                Sign in with Microsoft
              </Button>
            </>
          )}

          {/* Info text */}
          <p className="mt-6 text-center text-xs text-text-tertiary">
            By signing in, you agree to access your Microsoft 365 content to
            provide personalized assistance.
          </p>

          {/* Features list */}
          <div className="mt-8 space-y-3">
            <Feature text="Search across SharePoint and OneDrive" />
            <Feature text="Get AI-powered answers with citations" />
            <Feature text="Upload and analyze documents" />
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-text-tertiary">
          Powered by Azure OpenAI and Microsoft Graph
        </p>
      </div>
    </div>
  );
}

function Feature({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 text-sm text-text-secondary">
      <svg
        className="h-5 w-5 flex-shrink-0 text-success"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      </svg>
      <span>{text}</span>
    </div>
  );
}
